import { IconAdjustmentsFilled, IconCheck, IconX } from "@tabler/icons-react";
import { TipoNotaRodo, usePrenotaStore, TriggerCard } from "@PN/inclusao";
import { TIPOS_NF_OPTIONS } from "@PN/tabela";
import { Combobox, cn } from "visuais";
import { useCallback, useMemo } from "react";

export const TipoCombobox = () => {
  const updateOpcoes = usePrenotaStore((s) => s.actions.draft.opcoes.update);
  const tipoNota = usePrenotaStore((s) => s.draft.opcoes.tipoNota);
  const pedidos = usePrenotaStore((s) => s.aux.pedidos);
  const rateios = usePrenotaStore((s) => s.draft.rateios);
  const especie = usePrenotaStore((s) => s.draft.documento.especie);
  const chaveNFe = usePrenotaStore((s) => s.draft.documento.chaveNFe);
  const anexos = usePrenotaStore((s) => s.draft.anexos);

  const handleTipoChange = useCallback(
    (value: string | undefined) => {
      updateOpcoes({ tipoNota: (value || "") as TipoNotaRodo });
    },
    [updateOpcoes]
  );

  // Regras específicas baseadas no tipo de operação
  const regrasEspecificas = useMemo(() => {
    if (!tipoNota) return [];

    const regras = [];

    // Tipos que exigem pedidos obrigatórios
    const tiposComPedidoObrigatorio = [
      "Revenda",
      "Materia Prima",
      "Collection",
    ];
    if (tiposComPedidoObrigatorio.includes(tipoNota)) {
      regras.push({
        id: "pedidos",
        label: "Pedidos",
        valida: pedidos && pedidos.length > 0,
        obrigatorio: true,
      });
    }

    // Tipos que exigem exatamente 1 rateio com 100%
    if (tiposComPedidoObrigatorio.includes(tipoNota)) {
      const temRateioUnico = rateios && rateios.length === 1;
      const rateio100Porcento =
        temRateioUnico && Math.abs(rateios[0]?.percentual - 100) <= 0.01;

      regras.push({
        id: "rateio",
        label: "Rateio",
        valida: rateio100Porcento,
        obrigatorio: true,
      });
    }

    // Regras por espécie de documento
    if (especie === "SPED" || especie === "CTE") {
      regras.push({
        id: "chave",
        label: "XML",
        valida: !!chaveNFe,
        obrigatorio: true,
      });
    } else if (especie && especie !== "SPED" && especie !== "CTE") {
      regras.push({
        id: "anexo",
        label: "Anexo",
        valida: anexos && anexos.length > 0,
        obrigatorio: true,
      });
    }

    return regras;
  }, [tipoNota, pedidos, rateios, especie, chaveNFe, anexos]);

  const selectedLabel = useMemo(() => {
    return (
      TIPOS_NF_OPTIONS.find((option) => option.value === tipoNota)?.label ||
      tipoNota
    );
  }, [tipoNota]);

  const trigger = (
    <TriggerCard
      icon={
        <IconAdjustmentsFilled className="size-4 text-cyan-600 dark:text-cyan-400" />
      }
      label="Tipo de Operação"
      isValid={!!tipoNota}
    >
      {/* Regras específicas do tipo */}
      {regrasEspecificas.length > 0 && (
        <div className="grid grid-cols-3 gap-1 mt-2">
          {regrasEspecificas.map((regra) => (
            <div
              key={regra.id}
              className={cn(
                "flex items-center gap-1 text-[8pt] px-1 py-0.5 rounded",
                regra.valida
                  ? "text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/20"
                  : "text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/20"
              )}
            >
              {regra.valida ? (
                <IconCheck className="h-2.5 w-2.5" />
              ) : (
                <IconX className="h-2.5 w-2.5" />
              )}
              <span className="truncate">{regra.label}</span>
            </div>
          ))}
        </div>
      )}
      {/* Nome do tipo */}
      <p
        className="text-base uppercase text-center font-semibold text-cyan-800 dark:text-cyan-200 truncate"
        title={selectedLabel}
      >
        {selectedLabel || "Selecionar"}
      </p>
    </TriggerCard>
  );

  return (
    <div className="h-full">
      <Combobox
        options={TIPOS_NF_OPTIONS}
        value={tipoNota || ""}
        onChange={handleTipoChange}
        placeholder="Tipo de operação"
        trigger={trigger}
      />
    </div>
  );
};
